<template>
  <div class="review-item">
    <div class="review-item__content" :class="{ 'is-nested': isNested }">
      <div>
        <div class="review-item__header">
          <p class="review-item__title">
            {{ review.question }}
          </p>
          <ReviewUserActions
            v-if="showUserActions"
            :user-actions="userActions"
          />
        </div>
        <div class="review-item__subheader">
          <StarsRating v-if="review.rating !== null" :rating="review.rating" />
          <span class="review-item__date" v-if="review.date">
            {{ `${$t("message.reviews.addedLabel")}: ${review.date}` }}
          </span>
        </div>
        <p class="review-item__text">
          {{ showMore ? review.comment : reviewText }}
        </p>
        <div class="review-item__actions">
          <ui-button v-if="questionsNumber" @click="toggleShowAnswers"
            >{{ $t("message.reviewBooster.additionalQuestions") }} ({{
              questionsNumber
            }})
            <div class="ml-3">
              <v-icon :class="{ 'rotate-180': showAnswers }">
                icon-arrow-down
              </v-icon>
            </div>
          </ui-button>
          <ui-button v-if="showMoreButtonVisible" @click="toggleShowMore"
            >{{ toggleButtonText }}
            <div class="ml-3">
              <v-icon :class="{ 'rotate-180': showMore }">
                icon-arrow-down
              </v-icon>
            </div></ui-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StarsRating from "@/components/ui/StarsRating";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";

export default {
  name: "ReviewBoosterReviewItem",
  components: {
    StarsRating,
    ReviewUserActions: () =>
      import("@/components/ReviewBooster/ReviewUserActions.vue")
  },
  props: {
    review: {
      required: true,
      type: Object
    },
    facility: {
      type: Object
    },
    userActions: {
      type: Object
    },
    isNested: {
      type: Boolean,
      default: false
    },
    questionsNumber: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      showMore: false,
      showAnswers: false
    };
  },
  computed: {
    showUserActions() {
      return (
        this.userActions && Object.values(this.userActions).some(item => !!item)
      );
    },
    reviewText() {
      return this.showMoreButtonVisible
        ? this.review.comment.slice(0, 200) + "..."
        : this.review.comment;
    },
    showMoreButtonVisible() {
      return this.review.comment.length > 200;
    },
    toggleButtonText() {
      return this.showMore
        ? this.$t("message.common.less")
        : this.$t("message.common.more");
    }
  },
  methods: {
    toggleShowMore() {
      this.showMore = !this.showMore;
      if (!this.showMore) return;
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.RB_REVIEW_LIST_SHOW_FULL_REVIEW
      });
    },
    toggleShowAnswers() {
      this.showAnswers = !this.showAnswers;
      this.$emit("show-answers", this.showAnswers);
      if (!this.showAnswers) return;
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.RB_REVIEW_LIST_SHOW_MORE_QUESTIONS
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.review-item {
  display: flex;

  &__actions {
    display: flex;
    justify-content: space-between;
  }

  &__subheader {
    display: flex;
    align-items: center;
    gap: $spacing * 2;
    margin-bottom: $spacing;
  }

  &__checkbox-section {
    flex-shrink: 0;
    margin-right: $spacing * 2;
    display: flex;

    &:after {
      content: "";
      display: block;
      height: 100%;
      width: 3px;
      background: $gray-200;
      margin-left: $spacing * 2;
      border-radius: 3px;
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
  }

  &__title {
    margin-bottom: $spacing * 3;
  }

  &__user-actions {
    display: flex;
    gap: $spacing * 2;
  }

  &__checkbox {
    margin-top: $spacing * 0.5;
    padding: 0;
    ::v-deep .v-input--selection-controls__input {
      margin: 0;
    }
  }

  &__content {
    padding-right: $spacing * 4;
    flex-grow: 1;

    &.is-nested {
      padding-top: $spacing * 3;
      display: flex;

      &:before {
        flex-shrink: 0;
        content: "";
        display: block;
        width: 3px;
        height: 100%;
        border-radius: 10px;
        background: $gray-800;
        margin-right: $spacing * 2;
      }
    }
  }

  &__text,
  &__date {
    @include font-size(14px);
  }

  &__date {
    color: $gray-600;
  }

  &__additional-data {
    width: 400px;
    flex-shrink: 0;
  }

  &__facility-data {
    display: flex;
    align-items: center;
  }

  &__facility-name,
  &__facility-address {
    @include font-size(12px);
  }
  &__facility-name {
    font-weight: 600;
  }
}
</style>
